import React from "react"
import { Layout } from "../components/common"
import { MetaData } from "../components/common/meta"

const PrivacyPage = ({ location }) => (
    <>
        <MetaData
            location={location}
            title="Open Racer Privacy Policy"
            description="How Open Racer handles account information, Google sign-in data, racing content and privacy requests."
        />
        <Layout>
            <main className="outer">
                <article
                    className="inner"
                    style={{
                        maxWidth: 840,
                        padding: "48px 0 64px",
                        lineHeight: 1.7,
                    }}
                >
                    <h1>Open Racer Privacy Policy</h1>
                    <p>Last updated: October 25, 2026</p>
                    <p>
                        Open Racer is operated by Mr Bojli Kft., Hungary. This
                        policy covers open-racer.com and its racing tools,
                        including editor.open-racer.com. For privacy questions
                        and requests, contact{" "}
                        <a href="mailto:support@open-racer.com">
                            support@open-racer.com
                        </a>
                        .
                    </p>

                    <h2>Account information and Google sign-in</h2>
                    <p>
                        We use Firebase Authentication and FirebaseUI for
                        sign-in. If you choose Google sign-in, Google provides
                        basic account information, including your name, email
                        address, profile picture and account identifier. We use
                        this information to authenticate you, create and manage
                        your Open Racer account, display your driver profile and
                        associate your racing lines and other activity with your
                        account. Google sign-in does not give Open Racer access
                        to your Gmail messages, Google Drive files or Google
                        Calendar. We do not receive your Google password. If you
                        use another sign-in provider, we receive the account
                        information it shares for authentication.
                    </p>
                    <p>
                        Account information is processed through Firebase
                        Authentication. Driver profile information is stored in
                        our Firebase database. Your driver profile, including
                        your display name, profile picture and username, can be
                        publicly visible. Your email address is used for account
                        and service purposes; it is not included in the public
                        driver profile created by the sign-in flow.
                    </p>

                    <h2>Racing content and information you provide</h2>
                    <p>
                        We process information you choose to provide, such as
                        racing lines, saved tracks, likes, telemetry, imported
                        recordings, business claims, sponsorship enquiries and
                        support messages. Racing recordings and telemetry may
                        contain location coordinates, timestamps and driving
                        data. Content you publish or share, and your associated
                        public profile, may be visible to other people. Avoid
                        including information you do not want to share in public
                        content.
                    </p>

                    <h2>Usage information, cookies and local storage</h2>
                    <p>
                        Our website and service providers process technical
                        information such as IP addresses, browser and device
                        information, pages visited, feature usage and error
                        reports. We use cookies and browser storage to support
                        authentication, remember interface information and
                        understand how the service is used. Google Analytics
                        supports usage measurement, and Sentry supports error
                        monitoring. You can manage cookies and local storage
                        through your browser; blocking them may affect sign-in
                        and other features.
                    </p>

                    <h2>How we use information</h2>
                    <p>
                        We use information to provide and improve track
                        discovery and racing tools, save your work, manage
                        accounts and subscriptions, respond to enquiries,
                        maintain security, prevent abuse and meet legal
                        obligations. Where applicable, our grounds for
                        processing include providing the service you request,
                        your consent, our legitimate interests in operating and
                        securing the service, and compliance with legal
                        obligations.
                    </p>

                    <h2>Service providers and other disclosures</h2>
                    <p>
                        We use service providers to operate Open Racer,
                        including Google/Firebase for authentication, databases,
                        storage, maps and analytics; Netlify for hosting; Sentry
                        for error monitoring; and Stripe for payment and
                        subscription processing. Payment details you enter are
                        handled by the payment provider. Providers process
                        information needed to deliver their services, subject to
                        their terms and privacy notices. We may also disclose
                        information when required by law or to protect the
                        security and rights of users and the service.
                    </p>
                    <p>
                        These providers may process information outside your
                        country, including outside the European Economic Area.
                        Where required, international processing is subject to
                        applicable transfer safeguards provided through our
                        service arrangements.
                    </p>

                    <h2>Retention and deletion</h2>
                    <p>
                        We retain information to provide your account and saved
                        content and for operational, security, billing and legal
                        purposes. You can request account deletion through the
                        Delete Account control in your profile or by emailing{" "}
                        <a href="mailto:support@open-racer.com">
                            support@open-racer.com
                        </a>
                        . Account deletion removes your authentication account,
                        public driver profile and associated racing-line and
                        telemetry records handled by that process. Billing and
                        audit records may be retained separately. Contact us
                        about other information you want removed or if account
                        deletion fails.
                    </p>
                    <p>
                        You can manage or revoke the Google connection in your{" "}
                        <a href="https://myaccount.google.com/connections">
                            Google Account settings
                        </a>
                        . Revoking access does not by itself delete information
                        already stored in Open Racer.
                    </p>

                    <h2>Your privacy choices and rights</h2>
                    <p>
                        Depending on applicable law, you may have rights to
                        access, correct, delete, restrict or object to
                        processing of your information, request a portable copy,
                        or withdraw consent where processing relies on consent.
                        To make a request, email{" "}
                        <a href="mailto:support@open-racer.com">
                            support@open-racer.com
                        </a>
                        . We may need to verify your identity to protect your
                        account. You may also complain to your local data
                        protection authority.
                    </p>

                    <h2>Changes and contact</h2>
                    <p>
                        We will update this page and its revision date when this
                        policy changes. For questions about this policy or our
                        handling of your information, contact Mr Bojli Kft. at{" "}
                        <a href="mailto:support@open-racer.com">
                            support@open-racer.com
                        </a>
                        .
                    </p>
                </article>
            </main>
        </Layout>
    </>
)

export default PrivacyPage
